import { useState, useCallback } from 'react';
import reviewApiService from '../services/reviewApiService';

/**
 * Review API operations hook
 * Handles API calls with loading and error states
 */
export const useReviewAPI = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Generic API call wrapper with loading and error handling
   */
  const apiCall = useCallback(async (apiFunction, ...args) => {
    try {
      setLoading(true);
      setError(null);
      const result = await apiFunction(...args);
      return result;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'An error occurred';
      setError(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get creator's campaign posts with pagination
   */
  const getCreatorCampaignPosts = useCallback(async (creatorId, options = {}) => {
    return apiCall(reviewApiService.getCreatorCampaignPosts, creatorId, options);
  }, [apiCall]);

  /**
   * Get all status options
   */
  const getStatusOptions = useCallback(async () => {
    return apiCall(reviewApiService.getStatusOptions);
  }, [apiCall]);

  /**
   * Add new status option
   */
  const addStatusOption = useCallback(async (status) => {
    return apiCall(reviewApiService.addStatusOption, status);
  }, [apiCall]);

  /**
   * Get creator reviews
   */
  const getCreatorReviews = useCallback(async (creatorId) => {
    return apiCall(reviewApiService.getCreatorReviews, creatorId);
  }, [apiCall]);

  /**
   * Save review
   */
  const saveReview = useCallback(async (reviewData) => {
    return apiCall(reviewApiService.saveReview, reviewData);
  }, [apiCall]);

  /**
   * Start review session with lazy loading
   */
  const startReviewSession = useCallback(async (creatorIds, options = {}) => {
    return apiCall(reviewApiService.startReviewSession, creatorIds, options);
  }, [apiCall]);

  /**
   * Get review session
   */
  const getReviewSession = useCallback(async (sessionId) => {
    return apiCall(reviewApiService.getSession, sessionId);
  }, [apiCall]);

  /**
   * Update review session
   */
  const updateReviewSession = useCallback(async (sessionId, sessionData) => {
    return apiCall(reviewApiService.updateSession, sessionId, sessionData);
  }, [apiCall]);

  /**
   * Get creators' recent tweets (batch) with options
   */
  const getCreatorsRecentTweets = useCallback(async (handles, options = {}) => {
    return apiCall(reviewApiService.getCreatorsRecentTweets, handles, options);
  }, [apiCall]);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    // State
    loading,
    error,

    // API Methods
    getCreatorCampaignPosts,
    getStatusOptions,
    addStatusOption,
    getCreatorReviews,
    saveReview,
    startReviewSession,
    getReviewSession,
    updateReviewSession,
    getCreatorsRecentTweets,

    // Lazy loading
    loadMoreCreators: useCallback(async (sessionId, options = {}) => {
      return apiCall(reviewApiService.loadMoreCreators, sessionId, options);
    }, [apiCall]),

    // Performance metrics
    getPerformanceMetrics: useCallback(async (period = '1h') => {
      return apiCall(reviewApiService.getPerformanceMetrics, period);
    }, [apiCall]),

    // Helpers
    clearError
  };
};